import { Directive, ElementRef, Input, OnInit, OnDestroy, Renderer2 } from '@angular/core';
import { FeatureCodes } from '../constants';

interface Feature {
  id: number;
  licensePlanId: number;
  featureId: number;
  featureCode: string;
  featureName: string;
  isEnable: boolean;
  moduleId: number;
}

@Directive({
  selector: '[piqetAngularSectionAccess]',
})
export class SectionAccessDirective implements OnInit, OnDestroy {
  @Input('piqetAngularSectionAccess') featureCode!:
    | string
    | FeatureCodes
    | string[]
    | FeatureCodes[];

  private mutationObserver!: MutationObserver;
  private isDisabled = false;
  private originalContent: string = '';
  private eventListeners: (() => void)[] = [];

  constructor(private el: ElementRef, private renderer: Renderer2) {}

  ngOnInit(): void {
    this.checkFeatureAccess();
  }

  ngOnDestroy(): void {
    if (this.mutationObserver) {
      this.mutationObserver.disconnect();
    }
    // Clean up event listeners
    this.eventListeners.forEach(cleanup => cleanup());
    this.eventListeners = [];
  }

  private checkFeatureAccess(): void {
    try {
      // Get features from localStorage
      const featuresJson = localStorage.getItem('featureAccess');

      if (!featuresJson) {
        console.warn('No features found in localStorage');
        this.disableElement();
        return;
      }

      let features: Feature[] = [];
      try {
        features = JSON.parse(featuresJson);
        if (!Array.isArray(features)) {
          console.error('Features data is not an array');
          this.disableElement();
          return;
        }
      } catch (parseError) {
        console.error('Error parsing features from localStorage:', parseError);
        this.disableElement();
        return;
      }

      // Normalize feature codes to array
      const featureCodes = this.normalizeFeatureCodes(this.featureCode);
      if (featureCodes.length === 0) {
        console.warn('No valid feature codes provided');
        this.disableElement();
        return;
      }

      // Check if any of the feature codes grant access
      const hasAccess = this.checkMultipleFeatureAccess(features, featureCodes);

      if (!hasAccess) {
        this.disableElement();
      }
    } catch (error) {
      console.error('Unexpected error in checkFeatureAccess:', error);
      this.disableElement();
    }
  }

  private normalizeFeatureCodes(
    input: string | FeatureCodes | string[] | FeatureCodes[]
  ): string[] {
    if (!input) {
      return [];
    }

    try {
      // If it's already an array, filter out invalid values
      if (Array.isArray(input)) {
        return input
          .filter(code => code != null && code !== '')
          .map(code => String(code).trim())
          .filter(code => code.length > 0);
      }

      // If it's a single value, convert to array
      const singleCode = String(input).trim();
      return singleCode.length > 0 ? [singleCode] : [];
    } catch (error) {
      console.error('Error normalizing feature codes:', error);
      return [];
    }
  }

  private checkMultipleFeatureAccess(features: Feature[], featureCodes: string[]): boolean {
    try {
      // Check if any of the provided feature codes grant access
      return featureCodes.some(code => {
        const feature = features.find(f => f && f.featureCode === code);
        return feature && feature.isEnable === true;
      });
    } catch (error) {
      console.error('Error checking multiple feature access:', error);
      return false;
    }
  }

  private disableElement(): void {
    try {
      const element = this.el.nativeElement;
      if (!element) {
        console.error('Element reference is null');
        return;
      }

      const tagName = element.tagName?.toLowerCase() || '';
      this.isDisabled = true;

      // Store original content for restoration monitoring
      this.originalContent = element.innerHTML || '';

      // Apply disabled styles/attributes based on element type
      this.applyDisabledState(element, tagName);

      // Set up DOM mutation monitoring
      this.setupMutationObserver(element);

      // Add event listeners to prevent interactions
      this.addSecurityListeners(element);
    } catch (error) {
      console.error('Error disabling element:', error);
    }
  }

  private applyDisabledState(element: HTMLElement, tagName: string): void {
    try {
      if (['input', 'button', 'select', 'textarea', 'fieldset'].includes(tagName)) {
        this.renderer.setAttribute(element, 'disabled', 'true');
        this.renderer.addClass(element, 'piqet-disabled');
      } else {
        // For other elements (div, span, etc.)
        this.renderer.setStyle(element, 'pointer-events', 'none');
        this.renderer.setStyle(element, 'cursor', 'not-allowed');
        this.renderer.setStyle(element, 'user-select', 'none');
        this.renderer.setStyle(element, 'opacity', '0.6');
        this.renderer.addClass(element, 'piqet-disabled');

        // Additional security: hide content for sensitive elements
        if (element.hasAttribute('data-sensitive')) {
          this.renderer.setStyle(element, 'visibility', 'hidden');
        }
      }
    } catch (error) {
      console.error('Error applying disabled state:', error);
    }
  }

  private setupMutationObserver(element: HTMLElement): void {
    try {
      this.mutationObserver = new MutationObserver(mutations => {
        if (!this.isDisabled) return;

        mutations.forEach(mutation => {
          try {
            // Check for attribute changes
            if (mutation.type === 'attributes') {
              this.enforceDisabledState(element);
            }

            // Check for class changes
            if (mutation.type === 'attributes' && mutation.attributeName === 'class') {
              if (!element.classList.contains('piqet-disabled')) {
                this.renderer.addClass(element, 'piqet-disabled');
              }
            }

            // Check for style changes
            if (mutation.type === 'attributes' && mutation.attributeName === 'style') {
              this.enforceDisabledState(element);
            }

            // Check for content changes (for sensitive data)
            if (mutation.type === 'childList' && element.hasAttribute('data-sensitive')) {
              if (element.innerHTML !== this.originalContent) {
                element.innerHTML = this.originalContent;
              }
            }
          } catch (mutationError) {
            console.error('Error processing mutation:', mutationError);
          }
        });
      });

      // Start observing
      this.mutationObserver.observe(element, {
        attributes: true,
        attributeOldValue: true,
        childList: true,
        subtree: true,
      });
    } catch (error) {
      console.error('Error setting up mutation observer:', error);
    }
  }

  private enforceDisabledState(element: HTMLElement): void {
    try {
      const tagName = element.tagName?.toLowerCase() || '';

      if (['input', 'button', 'select', 'textarea', 'fieldset'].includes(tagName)) {
        if (!element.hasAttribute('disabled')) {
          this.renderer.setAttribute(element, 'disabled', 'true');
        }
      } else {
        // Re-apply styles if they've been modified
        const computedStyle = window.getComputedStyle(element);

        if (computedStyle.pointerEvents !== 'none') {
          this.renderer.setStyle(element, 'pointer-events', 'none');
        }

        if (parseFloat(computedStyle.opacity) > 0.6) {
          this.renderer.setStyle(element, 'opacity', '0.6');
        }

        if (computedStyle.cursor !== 'not-allowed') {
          this.renderer.setStyle(element, 'cursor', 'not-allowed');
        }

        if (computedStyle.userSelect !== 'none') {
          this.renderer.setStyle(element, 'user-select', 'none');
        }
      }

      // Ensure class is still present
      if (!element.classList.contains('piqet-disabled')) {
        this.renderer.addClass(element, 'piqet-disabled');
      }
    } catch (error) {
      console.error('Error enforcing disabled state:', error);
    }
  }

  private addSecurityListeners(element: HTMLElement): void {
    try {
      // Prevent common interaction events
      const events = [
        'click',
        'dblclick',
        'mousedown',
        'mouseup',
        'keydown',
        'keyup',
        'focus',
        'contextmenu',
      ];

      events.forEach(eventType => {
        const cleanup = this.renderer.listen(element, eventType, event => {
          if (this.isDisabled) {
            event.preventDefault();
            event.stopPropagation();
            event.stopImmediatePropagation();
            console.warn(`Blocked ${eventType} event on disabled element`);
            return false;
          }
        });
        this.eventListeners.push(cleanup);
      });

      // Additional protection for form elements
      const tagName = element.tagName?.toLowerCase() || '';
      if (['input', 'select', 'textarea'].includes(tagName)) {
        const cleanup = this.renderer.listen(element, 'input', event => {
          if (this.isDisabled) {
            event.preventDefault();
            const target = event.target as HTMLInputElement;
            if (target) {
              target.value = target.defaultValue || '';
            }
            return false;
          }
        });
        this.eventListeners.push(cleanup);
      }
    } catch (error) {
      console.error('Error adding security listeners:', error);
    }
  }
}
